import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as invoicesApi from '../api/invoicesApi';
import { Card, EmptyState, ErrorBanner, Spinner, TableSkeleton } from '../components/ui';
import { IconAlert, IconX } from '../components/icons';
import {
  INVOICE_ACCEPT,
  INVOICE_MAX_BYTES,
  INVOICE_MIME_TYPES,
  InvoiceUploadResult,
  UploadedInvoice,
} from '../types/invoice';
import { formatDate, formatDateTime, formatINRPrecise } from '../utils/format';

/** YYYY-MM-DD in local time — toISOString() would file an early-morning IST
 *  upload under the previous day. */
function toInputDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TODAY = toInputDate(new Date());

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Rejects before upload so a 30MB file isn't pushed over the wire only to be
 *  refused. The server re-checks both — this is convenience, not the guard. */
function validateFile(file: File): string | null {
  if (!INVOICE_MIME_TYPES.includes(file.type)) {
    return `"${file.name}" is not a PDF, JPG or PNG`;
  }
  if (file.size > INVOICE_MAX_BYTES) {
    return `"${file.name}" is ${formatBytes(file.size)} — the limit is 10MB`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty`;
  }
  return null;
}

const BLANK_FORM = {
  invoice_date: TODAY,
  supplier_name: '',
  amount: '',
  description: '',
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<UploadedInvoice[]>([]);
  const [driveConfigured, setDriveConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [supplier, setSupplier] = useState('');
  // Applied separately from the input so typing a supplier name doesn't fire a
  // request per keystroke — the filter runs on submit.
  const [appliedSupplier, setAppliedSupplier] = useState('');

  // Upload form
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [lastUpload, setLastUpload] = useState<InvoiceUploadResult | null>(null);

  const [dragging, setDragging] = useState(false);
  // Nested children fire dragleave as the pointer crosses them, so track depth
  // rather than a boolean or the highlight flickers across the zone's contents.
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await invoicesApi.getInvoices({
        from,
        to,
        supplier_name: appliedSupplier,
      });
      setInvoices(res.invoices);
      setDriveConfigured(res.drive_configured);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, appliedSupplier]);

  useEffect(() => {
    load();
  }, [load]);

  const acceptFile = (candidate: File | undefined) => {
    if (!candidate) return;
    const problem = validateFile(candidate);
    if (problem) {
      setUploadError(problem);
      setFile(null);
      return;
    }
    setUploadError('');
    setLastUpload(null);
    setFile(candidate);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadError('Choose a file to upload');
      return;
    }
    if (!form.invoice_date) {
      setUploadError('Pick the invoice date');
      return;
    }
    if (form.amount.trim() !== '') {
      const amount = Number(form.amount);
      if (!Number.isFinite(amount) || amount < 0) {
        setUploadError('Enter an amount of 0 or more');
        return;
      }
    }

    setUploading(true);
    setUploadError('');
    setProgress(0);
    try {
      const result = await invoicesApi.uploadInvoice(
        {
          file,
          invoice_date: form.invoice_date,
          supplier_name: form.supplier_name.trim() || undefined,
          amount: form.amount.trim() || undefined,
          description: form.description.trim() || undefined,
        },
        setProgress
      );
      setLastUpload(result);
      setFile(null);
      setForm({ ...BLANK_FORM, invoice_date: TODAY });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (err) {
      setUploadError(apiErrorMessage(err));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Invoices</h1>
        <p className="text-sm text-sub">
          Supplier invoices, filed automatically into Google Drive by month
        </p>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* A missing service account is a deployment gap, not a user error — say
          so plainly rather than letting the upload fail with a 503. */}
      {!driveConfigured && (
        <div className="flex items-start gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">Google Drive isn't connected.</span> Uploads will be
            rejected until the GOOGLE_OAUTH_* secrets are set on the ecommerce service — run
            scripts/google-oauth-setup.cjs to authorize an account. Invoices already filed are
            still listed below.
          </p>
        </div>
      )}

      {/* ── Upload ─────────────────────────────────────────────────────────── */}
      <Card title="Upload an invoice">
        <div className="space-y-4">
          {/* Drop zone. Clicking anywhere in it opens the picker, so the
              keyboard path and the mouse path lead to the same input. */}
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              dragDepth.current += 1;
              setDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              dragDepth.current -= 1;
              if (dragDepth.current <= 0) setDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Choose an invoice file, or drop one here"
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragging
                ? 'border-gold bg-gold/10'
                : 'border-edge hover:border-gold/50 hover:bg-edge/20'
            }`}
          >
            {file ? (
              <>
                <p className="text-sm font-medium">{file.name}</p>
                <p className="text-xs text-sub">{formatBytes(file.size)}</p>
                <button
                  type="button"
                  className="btn-ghost !px-2 !py-1 !text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <IconX className="h-3.5 w-3.5" />
                  Remove
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">Drop an invoice here, or click to browse</p>
                <p className="text-xs text-sub">PDF, JPG or PNG — up to 10MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={INVOICE_ACCEPT}
              className="hidden"
              onChange={(e) => acceptFile(e.target.files?.[0])}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="label" htmlFor="inv-date">
                Invoice date
              </label>
              <input
                id="inv-date"
                type="date"
                className="input"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="inv-supplier">
                Supplier name
              </label>
              <input
                id="inv-supplier"
                className="input"
                placeholder="e.g. Acme Packaging"
                value={form.supplier_name}
                onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="inv-amount">
                Amount (₹)
              </label>
              <input
                id="inv-amount"
                type="number"
                min={0}
                step="0.01"
                className="input"
                placeholder="e.g. 4500.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="inv-description">
                Description
              </label>
              <input
                id="inv-description"
                className="input"
                placeholder="What it was for"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>

          {/* The date drives the Drive folder, so show where the file will land
              before it is sent — a mistyped year is obvious here and invisible
              once filed. */}
          {form.invoice_date && (
            <p className="text-xs text-sub">
              Files to{' '}
              <span className="font-medium text-ink">
                TPC Invoices/{form.invoice_date.slice(0, 4)}/
                {new Date(`${form.invoice_date}T00:00:00`).toLocaleDateString('en-GB', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </p>
          )}

          {uploadError && <ErrorBanner message={uploadError} />}

          {lastUpload?.invoice && (
            <div className="rounded-xl border border-ok/40 bg-ok/10 px-4 py-3 text-sm">
              <p className="font-semibold text-ok">Filed to Google Drive</p>
              <p className="mt-1 text-sub">
                {lastUpload.invoice.filename}
                {lastUpload.drive_path && <> → {lastUpload.drive_path}</>}
              </p>
              <a
                href={lastUpload.invoice.drive_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block font-medium text-gold underline underline-offset-2"
              >
                Open in Drive ↗
              </a>
            </div>
          )}

          {uploading && (
            <div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-edge">
                <div
                  className="h-full bg-gold transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-sub">
                {progress < 100
                  ? `Uploading — ${progress}%`
                  : 'Uploaded — filing to Drive…'}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              className="btn-gold"
              onClick={handleUpload}
              disabled={uploading || !file || !form.invoice_date}
            >
              {uploading && <Spinner />}
              Upload invoice
            </button>
          </div>
        </div>
      </Card>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Card bodyClassName="!py-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSupplier(supplier.trim());
          }}
        >
          <div>
            <label className="label" htmlFor="inv-from">
              From
            </label>
            <input
              id="inv-from"
              type="date"
              className="input !w-auto"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="inv-to">
              To
            </label>
            <input
              id="inv-to"
              type="date"
              className="input !w-auto"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="inv-supplier-filter">
              Supplier
            </label>
            <input
              id="inv-supplier-filter"
              className="input !w-auto"
              placeholder="Any supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-outline">
            Apply
          </button>
          {(from || to || appliedSupplier) && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setFrom('');
                setTo('');
                setSupplier('');
                setAppliedSupplier('');
              }}
            >
              Clear
            </button>
          )}
        </form>
      </Card>

      {/* ── List ───────────────────────────────────────────────────────────── */}
      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={5} />
        ) : invoices.length === 0 ? (
          <EmptyState
            message="No invoices yet"
            hint="Upload a supplier invoice above and it will be filed into Drive by month."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Invoice date</th>
                  <th className="th">Supplier</th>
                  <th className="th">Description</th>
                  <th className="th text-right">Amount</th>
                  <th className="th">Uploaded</th>
                  <th className="th">File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="transition-colors hover:bg-edge/20">
                    <td className="td whitespace-nowrap text-sub">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="td font-medium">{invoice.supplier_name || '—'}</td>
                    <td className="td text-sub">
                      {invoice.description ? (
                        <span className="block max-w-[260px] truncate" title={invoice.description}>
                          {invoice.description}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="td text-right font-medium">
                      {invoice.amount != null ? (
                        formatINRPrecise(invoice.amount)
                      ) : (
                        <span className="text-sub">—</span>
                      )}
                    </td>
                    <td className="td whitespace-nowrap text-sub">
                      {formatDateTime(invoice.uploaded_at)}
                    </td>
                    <td className="td">
                      <a
                        href={invoice.drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gold underline underline-offset-2"
                        title={invoice.filename}
                      >
                        Open ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
